import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const reportApi = {
  teamSummary: (year, month) => api.get('/reports/summary/team', { params: { year, month } }),
  projectSummary: (year, month) => api.get('/reports/summary/by-project', { params: { year, month } }),
  downloadMyMonthly: (year, month) => api.get('/reports/my-monthly', { params: { year, month }, responseType: 'blob' }),
  downloadEmployeeMonthly: (empId, year, month) => api.get('/reports/employee-monthly', { params: { employeeId: empId, year, month }, responseType: 'blob' }),
  downloadCompanyMonthly: (year, month) => api.get('/reports/company-monthly', { params: { year, month }, responseType: 'blob' }),
};

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function ReportsPage() {
  const { isAdmin, isHR, isManager } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [tab, setTab] = useState('team');
  const [teamData, setTeamData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => { loadReport(); }, [year, month, tab]);

  const loadReport = async () => {
    setLoading(true);
    try {
      if (tab === 'team' || tab === 'employee') {
        const res = await reportApi.teamSummary(year, month);
        if (res.data.success) setTeamData(res.data.data);
      }
      if (tab === 'project') {
        const res = await reportApi.projectSummary(year, month);
        if (res.data.success) setProjectData(res.data.data);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const downloadExcel = async (type, empId, empName) => {
    setDownloading(type + (empId || ''));
    try {
      let res;
      if (type === 'my') res = await reportApi.downloadMyMonthly(year, month);
      else if (type === 'employee') res = await reportApi.downloadEmployeeMonthly(empId, year, month);
      else if (type === 'company') res = await reportApi.downloadCompanyMonthly(year, month);

      const monthName = MONTHS[month - 1];
      let filename;
      if (type === 'employee' && empName) {
        const safeName = empName.replace(/[^a-zA-Z0-9]/g, '_');
        filename = `Timesheet_${safeName}_${monthName}_${year}.xlsx`;
      } else if (type === 'my') {
        filename = `My_Timesheet_${monthName}_${year}.xlsx`;
      } else {
        filename = `Company_Report_${monthName}_${year}.xlsx`;
      }

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error downloading report');
    }
    setDownloading(null);
  };

  const getStatusColor = (status) => {
    if (status === 'HR_APPROVED' || status === 'MANAGER_APPROVED') return '#16a34a';
    if (status === 'SUBMITTED') return '#d97706';
    if (status === 'MANAGER_REJECTED' || status === 'HR_REJECTED') return '#dc2626';
    return '#6b7280';
  };

  const tabs = [];
  tabs.push({ key: 'team', label: 'Team Summary' });
  tabs.push({ key: 'employee', label: 'Employee Detail' });
  tabs.push({ key: 'project', label: 'By Project' });

  return (
    <div>
      <div className="page-header">
        <h1>Reports</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="form-control" style={{ width: 140, padding: '6px 8px' }} value={month}
            onChange={e => setMonth(parseInt(e.target.value))}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="form-control" style={{ width: 90, padding: '6px 8px' }} value={year}
            onChange={e => setYear(parseInt(e.target.value))}>
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {(isAdmin || isHR) && (
            <button className="btn btn-primary" onClick={() => downloadExcel('company')}
              disabled={downloading === 'company'}>
              📥 {downloading === 'company' ? 'Downloading...' : 'Company Report'}
            </button>
          )}
          <button className="btn btn-outline" onClick={() => downloadExcel('my')}
            disabled={downloading === 'my'}>
            📥 My Report
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid #e5e7eb' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px', fontSize: 14, fontWeight: tab === t.key ? 700 : 400,
              border: 'none', cursor: 'pointer', borderRadius: '8px 8px 0 0',
              background: tab === t.key ? '#2563eb' : 'transparent',
              color: tab === t.key ? '#fff' : '#6b7280',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : (
        <>
          {/* ── Team Summary Tab ── */}
          {tab === 'team' && teamData && (
            <div>
              {/* KPI Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
                <KpiCard label="Total Employees" value={teamData.totalEmployees} color="#2563eb" />
                <KpiCard label="Total Hours" value={parseFloat(teamData.totalHours).toFixed(1)} color="#16a34a" />
                <KpiCard label="Fully Approved" value={teamData.approved} color="#16a34a" />
                <KpiCard label="Missing / Pending" value={teamData.missing + teamData.submitted} color="#dc2626" />
              </div>

              {/* Employee Table */}
              <div className="card">
                <div className="card-header"><h3>Employee Hours — {MONTHS[month - 1]} {year}</h3></div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Employee</th><th>Team</th><th>Hours</th><th>Scheduled</th><th>Variance</th>
                        <th>PTO</th><th>Vacation</th><th>Sick</th>
                        <th>Approved</th><th>Submitted</th><th>Missing</th><th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(teamData.employees || []).map((e, i) => (
                        <tr key={i}>
                          <td>
                            <strong>{e.name}</strong>
                            <div style={{ fontSize: 11, color: '#6b7280' }}>{e.employeeId} • {e.jobTitle || '—'}</div>
                          </td>
                          <td style={{ fontSize: 13 }}>{e.team}</td>
                          <td style={{ fontWeight: 700 }}>{parseFloat(e.totalHours).toFixed(1)}</td>
                          <td>{parseFloat(e.scheduledHours).toFixed(1)}</td>
                          <td style={{ color: parseFloat(e.variance) >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                            {parseFloat(e.variance) >= 0 ? '+' : ''}{parseFloat(e.variance).toFixed(1)}
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {e.leave?.PTO ? <span title={`Accrued: ${e.leave.PTO.accrued} | Used: ${e.leave.PTO.used} | Pending: ${e.leave.PTO.pending}`}>
                              {parseFloat(e.leave.PTO.available).toFixed(0)}/{parseFloat(e.leave.PTO.accrued).toFixed(0)}
                            </span> : '—'}
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {e.leave?.VACATION ? <span title={`Accrued: ${e.leave.VACATION.accrued} | Used: ${e.leave.VACATION.used}`}>
                              {parseFloat(e.leave.VACATION.available).toFixed(0)}/{parseFloat(e.leave.VACATION.accrued).toFixed(0)}
                            </span> : '—'}
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {e.leave?.SICK ? <span title={`Accrued: ${e.leave.SICK.accrued} | Used: ${e.leave.SICK.used}`}>
                              {parseFloat(e.leave.SICK.available).toFixed(0)}/{parseFloat(e.leave.SICK.accrued).toFixed(0)}
                            </span> : '—'}
                          </td>
                          <td style={{ color: '#16a34a' }}>{e.weeksApproved}/{e.totalWeeks}</td>
                          <td style={{ color: '#d97706' }}>{e.weeksSubmitted}</td>
                          <td style={{ color: '#dc2626' }}>{e.weeksMissing}</td>
                          <td>
                            <span style={{
                              padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                              color: '#fff', background: getStatusColor(e.status)
                            }}>
                              {e.status?.replace(/_/g, ' ')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── Employee Detail Tab ── */}
          {tab === 'employee' && teamData && (
            <div>
              <div className="card">
                <div className="card-header"><h3>Download Individual Reports — {MONTHS[month - 1]} {year}</h3></div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Employee</th><th>Team</th><th>Total Hours</th><th>Status</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(teamData.employees || []).map((e, i) => (
                        <tr key={i}>
                          <td>
                            <strong>{e.name}</strong>
                            <div style={{ fontSize: 11, color: '#6b7280' }}>{e.employeeId}</div>
                          </td>
                          <td>{e.team}</td>
                          <td style={{ fontWeight: 700 }}>{parseFloat(e.totalHours).toFixed(1)}h</td>
                          <td>
                            <span style={{
                              padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                              color: '#fff', background: getStatusColor(e.status)
                            }}>
                              {e.status?.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td>
                            <button className="btn btn-sm btn-outline"
                              onClick={() => downloadExcel('employee', e.employeeDbId, e.name)}
                              disabled={downloading === 'employee' + e.employeeDbId}>
                              📥 {downloading === 'employee' + e.employeeDbId ? '...' : 'Download'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── By Project Tab ── */}
          {tab === 'project' && projectData && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <BreakdownCard title="Hours by Project" data={projectData.byProject} />
              <BreakdownCard title="Hours by Activity" data={projectData.byActivity} />
              <BreakdownCard title="Hours by TRC" data={projectData.byTrc} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, color }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: 20 }}>
      <div style={{ fontSize: 32, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{label}</div>
    </div>
  );
}

function BreakdownCard({ title, data }) {
  const total = (data || []).reduce((s, d) => s + parseFloat(d.hours || 0), 0);

  return (
    <div className="card">
      <div className="card-header"><h3>{title}</h3></div>
      <div className="card-body">
        {(!data || data.length === 0) ? (
          <div className="empty-state">No data for this period</div>
        ) : (
          <div>
            {data.map((d, i) => {
              const pct = total > 0 ? (parseFloat(d.hours) / total * 100) : 0;
              return (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{d.name}</span>
                    <span>{parseFloat(d.hours).toFixed(1)}h ({pct.toFixed(0)}%)</span>
                  </div>
                  <div style={{ background: '#e5e7eb', borderRadius: 4, height: 8 }}>
                    <div style={{
                      width: pct + '%', height: '100%', borderRadius: 4,
                      background: i === 0 ? '#2563eb' : i === 1 ? '#16a34a' : i === 2 ? '#d97706' : '#6b7280'
                    }} />
                  </div>
                </div>
              );
            })}
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 8, marginTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 13 }}>
              <span>Total</span>
              <span>{total.toFixed(1)}h</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
